import { heicTo } from 'heic-to'
import React, { useState, useEffect } from 'react'
import styled from 'styled-components/macro'

// Utility function to get file extension from URL
const getFileExtension = url => {
  try {
    const pathname = new URL(url).pathname
    const extension = pathname.split('.').pop()?.toLowerCase()
    return extension || null
  } catch {
    // Fallback for relative URLs or invalid URLs
    const extension = url.split('.').pop()?.toLowerCase()
    return extension || null
  }
}

// Check specifically for HEIC/HEIF files
const isHeicFile = url => {
  const extension = getFileExtension(url)
  return extension === 'heic' || extension === 'heif'
}

export const ImageLoader = ({ src, alt = '' }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [convertedSrc, setConvertedSrc] = useState(null)

  const isHeic = src ? isHeicFile(src) : false

  // Get the actual source to use (converted HEIC or original)
  const imageSrc = isHeic && convertedSrc ? convertedSrc : src

  // Additional processing for HEIC/HEIF files
  const processHeicFile = async url => {
    setIsProcessing(true)
    try {
      // Fetch the HEIC file
      const response = await fetch(url)
      const blob = await response.blob()

      // Convert HEIC to JPEG using heic2any
      const convertedBlob = await heicTo({
        blob,
        type: 'image/jpeg',
        quality: 0.8,
      })

      // Create object URL for the converted image
      const convertedUrl = URL.createObjectURL(convertedBlob)
      setConvertedSrc(convertedUrl)
    } catch (error) {
      console.error('Error processing HEIC file:', error)
      // If conversion fails, we'll fall back to trying the original src
    } finally {
      setIsProcessing(false)
    }
  }

  // Trigger HEIC processing when src changes and it's a HEIC file
  useEffect(() => {
    if (src && isHeic) {
      processHeicFile(src)
    }
  }, [src, isHeic])

  // Cleanup object URLs when component unmounts or src changes
  useEffect(() => {
    return () => {
      if (convertedSrc) {
        URL.revokeObjectURL(convertedSrc)
      }
    }
  }, [convertedSrc])

  return (
    <Wrapper>
      <img
        src={imageSrc}
        alt={alt}
        className={`smooth-image ${imageLoaded ? 'visible' : 'hidden'}`}
        onLoad={() => setImageLoaded(true)}
      />
      {isHeic && isProcessing && <div className='heic-processing'>Converting HEIC image...</div>}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
  .smooth-image {
    transition: opacity 0.3s ease-in-out;
  }
  .hidden {
    opacity: 0;
  }
  .visible {
    opacity: 1;
  }
  .placeholder {
    background: rgba(0, 0, 0, 0.1);
    height: 100%;
    width: 100%;
  }
`
